import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import { ChevronDown, X } from "lucide-react";
import Button from './Button.js';
import { NavIcon } from './sidebarIcons.js';
import {
  hasAttendanceAccess,
  hasMlAgentCommissionAccess,
  hasExternalCommissionAccess,
  hasPaidAmountSettlementAccess,
  isContractPartyAdmin,
  hasShipmentCrossingAccess,
  hasOgclTransferRequestMenuAccess,
  hasLocationHubBookingAccess,
  shouldHideMlSheetDispatch,
} from '../../utils/networkMode.js';
import companySettlementService from '../../services/companySettlementService.js';
import { useSidebarMobile } from '../../context/SidebarMobileContext.js';
import AppBrandLogo from './AppBrandLogo.js';
import { useCompanyLogo } from '../../hooks/useCompanyLogo.js';
import { isRoleModuleEnabled } from '../../utils/roleModuleAccess.js';

/** Mobile: keep sidebar + section open when navigating within these dropdown groups. */
const MOBILE_KEEP_SIDEBAR_OPEN_IDS = new Set([
  'shipments',
  'shipmentCommission',
  'settlements',
  'userManagement',
  'contractManagement',
]);

/** Accountant sidebar: finance tabs plus user, HR, and location hub access. */
const ACCOUNTANT_ALLOWED_ITEM_IDS = new Set([
  'dashboard',
  'shipments',
  'masterLoadingSheet',
  'masterLoadingSheetExternal',
  'shipmentCrossing',
  'updateStatus',
  'trackShipment',
  'cashbook',
  'settlements',
  'dailyPlDesk',
  'plStatutory',
  'expenseManagement',
  'weeklyBills',
  'misReports',
  'analyticalReports',
  'userManagement',
  'hrWorkforce',
  'locationHubVehicle',
]);

const PENDING_DOT_PATHS = {
  companySettlementApprovals: '/company-settlement-approvals',
  companySettlements: '/company-settlements',
};

const NavPendingDot = () => (
  <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
    <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
  </span>
);

const NavLabel = ({ label, showDot }) => (
  <span className="flex items-center gap-2 min-w-0 flex-1">
    <span className="truncate md:truncate-none md:leading-snug">{label}</span>
    {showDot ? <NavPendingDot /> : null}
  </span>
);

const Sidebar = () => {
  const { t } = useTranslation();
  const { isOpen, closeSidebar } = useSidebarMobile();
  const [menuItems, setMenuItems] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [pendingNavDots, setPendingNavDots] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const companyLogo = useCompanyLogo(user);
  const navRef = useRef(null);

  const pathToNavKey = (path) => path.replace(/\//g, '--');

  useEffect(() => {
    if (user) {
      if (user.role === 'Owner') {
        const companyAccess = user.company_features || user.company_can_access || {};
        const hasExpensesAccess = companyAccess.expenses !== false;
        const ownerItems = [
          {
            id: 'dashboard',
            labelKey: 'sidebar.dashboard',
            path: '/owner-dashboard',
            visible: true,
            type: 'single',
          },
          {
            id: 'misReports',
            labelKey: 'sidebar.misReports',
            path: '/reports',
            visible: true,
            type: 'single',
          },
          {
            id: 'weeklyBills',
            labelKey: 'sidebar.weeklyBills',
            path: '/expenses/weekly-bills',
            visible: hasExpensesAccess,
            type: 'single',
          },
          {
            id: 'expenseManagement',
            labelKey: 'sidebar.expenseManagement',
            path: '/expenses',
            visible: hasExpensesAccess,
            type: 'single',
          },
          {
            id: 'userManagement',
            labelKey: 'sidebar.userManagement',
            path: '/users',
            visible: true,
            type: 'single',
          },
          {
            id: 'companySettings',
            labelKey: 'sidebar.companySettings',
            path: '/company-settings',
            visible: true,
            type: 'single',
          },
        ];
        setMenuItems(ownerItems.filter((item) => item.visible));
        return;
      }

      if (isContractPartyAdmin(user)) {
        const contractPartyItems = [
          {
            id: 'dashboard',
            labelKey: 'sidebar.dashboard',
            path: '/contract-party-dashboard',
            visible: true,
            type: 'single',
          },
          {
            id: 'masterLoadingSheet',
            labelKey: 'sidebar.masterLoadingSheet',
            path: '/ml-sheets',
            visible: true,
            type: 'single',
          },
          {
            id: 'agentCommission',
            labelKey: 'sidebar.agentCommissionPayments',
            path: '/ml-commission-payments',
            visible: true,
            type: 'single',
          },
          {
            id: 'userManagement',
            labelKey: 'sidebar.userManagement',
            path: '/users',
            visible: true,
            type: 'single',
          },
        ];
        setMenuItems(contractPartyItems.filter((item) => item.visible));
        return;
      }

      const isSystemAdmin = user.role === 'System Admin';
      const isAdmin = ['System Admin', 'Admin'].includes(user.role);
      const isAccountant = user.role === 'Accountant';
      const isHubAdmin = user.role === 'Hub Admin';
      const isAdminOrAccountant = ['System Admin', 'Admin', 'Accountant'].includes(user.role);
      // Hub Admin uses admin shell for ops modules (scoped on backend)
      const isOpsAdmin = isAdminOrAccountant || isHubAdmin;
      const shipmentRoles = ['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin'];

      const companyAccess = user.company_features || user.company_can_access || {};
      const allowModule = (key) => isRoleModuleEnabled(user, key);

      const hasShipmentAccess = isSystemAdmin || companyAccess.shipment || false;
      const hasInventoryAccess = isSystemAdmin || companyAccess.inventory || false;
      const hasExpensesAccess = isSystemAdmin || companyAccess.expenses || false;
      const hasLoansAccess = (isSystemAdmin || companyAccess.loans || false) && allowModule('loans');
      const hasCommissionAccess = (isSystemAdmin || companyAccess.commission || false) && allowModule('commission');
      const isExternalNetwork = user.network_mode === 'EXTERNAL';
      const showMlAgentCommission = hasMlAgentCommissionAccess(user) && allowModule('commission');
      const showExternalCommission = hasExternalCommissionAccess(user) && allowModule('commission');
      const showPaidAmountSettlement = hasPaidAmountSettlementAccess(user);
      const showOgclTransferRequests = hasOgclTransferRequestMenuAccess(user);
      const hideMlDispatch = shouldHideMlSheetDispatch(user);
      const hubBookingAllowed = hasLocationHubBookingAccess(user);
      const externalDashboardPath = '/external-commission-dashboard';
      const hasExternalOnContract = isSystemAdmin || companyAccess.external_on_contract || false;
      const hasOnContract = (isSystemAdmin || companyAccess.on_contract || false) && allowModule('on_contract');
      const hasVehicleOwnerAccess = hasCommissionAccess || hasOnContract
        || (isExternalNetwork && (companyAccess.external_commission || companyAccess.commission))
        || hasExternalOnContract;
      const hasLocationHubVehicleMenu =
        isOpsAdmin && (hasShipmentAccess || hasVehicleOwnerAccess);
      const hasOnContractTruck = (isSystemAdmin || companyAccess.on_contract_truck || false) && allowModule('on_contract_truck');
      const hasCashbookAccess = (isSystemAdmin || companyAccess.cashbook || false) && allowModule('cashbook');
      // Missing key = enabled (matches backend default); hide only when explicitly false
      const hasAnalyticalReports = (isSystemAdmin || companyAccess.analytical_reports !== false) && allowModule('analytical_reports');
      const hasAgentNetwork = isSystemAdmin || (companyAccess.agent_network && user.network_mode === 'EXTERNAL');
      const hasMyPartnerships = Boolean(companyAccess.my_partnerships) && allowModule('my_partnerships');
      const hasExternalML = Boolean(companyAccess.external_ml);
      const hasAttendanceAccessFlag = hasAttendanceAccess(user);
      const ownBranchAgent = user.role === 'Agent'
        && !user.party_id
        && String(user.location_hub_ownership || '').toUpperCase() === 'OWN';
      const showExpenses = (
        hasExpensesAccess
        && ['System Admin', 'Admin', 'Accountant'].includes(user.role)
        && allowModule('expenses')
      ) || (ownBranchAgent && hasExpensesAccess);
      const showLoans = hasLoansAccess && isAdminOrAccountant;
      const showDailyPayments = hasExpensesAccess && (
        user.can_execute_payments || user.role === 'System Admin'
      );
      const showWeeklyBills = hasExpensesAccess && isAdminOrAccountant;
      const isAgent = user.role === 'Agent';
      const showAgentPaidSettlement = isAgent && hasShipmentAccess && showPaidAmountSettlement && hubBookingAllowed;
      const showAgentReturnToCompany = isAgent && hasShipmentAccess && !isExternalNetwork;
      const showAgentSettlements = showAgentPaidSettlement || showAgentReturnToCompany;
      const showDispatchOgcl = hasShipmentAccess && showOgclTransferRequests && !isExternalNetwork;
      const showMlDispatchNav =
        hasShipmentAccess && shipmentRoles.includes(user.role) && !isExternalNetwork && !hideMlDispatch;
      const showOpsSettlements = isAdminOrAccountant && hasShipmentAccess && (
        (showPaidAmountSettlement && hubBookingAllowed)
        || !isExternalNetwork
      );

      const items = [
        {
          id: 'dashboard',
          labelKey: 'sidebar.dashboard',
          path: user.role === 'Owner' ? '/owner-dashboard'
                : user.role === 'Accountant' ? '/accountant-dashboard'
                : user.role === 'Agent' ? '/agent-dashboard'
                : user.role === 'Driver' ? '/driver-dashboard'
                : user.role === 'System Admin' ? '/system-admin-dashboard'
                : user.role === 'Contract Party Admin' ? '/contract-party-dashboard'
                : '/admin-dashboard',
          visible: true,
          type: 'single'
        },
        {
          id: 'companyManagement',
          labelKey: 'sidebar.companyManagement',
          visible: isSystemAdmin,
          type: 'dropdown',
          children: [
            { labelKey: 'sidebar.companyProfile', path: '/companies', visible: isSystemAdmin },
            { labelKey: 'sidebar.companySubscription', path: '/company-subscriptions', visible: isSystemAdmin },
            { labelKey: 'sidebar.whatsappCounter', path: '/company-whatsapp-counters', visible: isSystemAdmin },
            { labelKey: 'sidebar.gstVerificationCounter', path: '/company-gst-verification-counters', visible: isSystemAdmin },
            { labelKey: 'sidebar.ewaybillCounter', path: '/company-ewaybill-counters', visible: isSystemAdmin }
          ]
        },
        {
          id: 'shipments',
          labelKey: 'sidebar.shipments',
          visible: hasShipmentAccess && shipmentRoles.includes(user.role) && allowModule('shipments'),
          type: 'dropdown',
          defaultPath: !hubBookingAllowed
            ? '/missing-shipments'
            : isHubAdmin
              ? '/payments'
              : '/shipments',
          children: [
            {
              labelKey: 'sidebar.shipment',
              path: '/shipments',
              visible: hasShipmentAccess && shipmentRoles.includes(user.role) && hubBookingAllowed && !isHubAdmin,
            },
            { labelKey: 'sidebar.shipmentPayment', path: '/payments', visible: hasShipmentAccess && shipmentRoles.includes(user.role) && hubBookingAllowed },
            { labelKey: 'sidebar.shipmentMissing', path: '/missing-shipments', visible: hasShipmentAccess && shipmentRoles.includes(user.role) },
            { labelKey: 'sidebar.shipmentReturn', path: '/returns', visible: hasShipmentAccess && shipmentRoles.includes(user.role) },
          ]
        },
        {
          id: 'masterLoadingSheet',
          labelKey: (showMlDispatchNav && showOgclTransferRequests)
            ? 'sidebar.masterLoadingSheet'
            : showOgclTransferRequests && !showMlDispatchNav
              ? 'sidebar.ogclTransferRequests'
              : 'sidebar.masterLoadingSheet',
          visible: (showMlDispatchNav || (showOgclTransferRequests && !isExternalNetwork))
            && shipmentRoles.includes(user.role)
            && !isExternalNetwork
            && allowModule('dispatch'),
          ...(showMlDispatchNav && showDispatchOgcl
            ? {
                type: 'dropdown',
                defaultPath: '/ml-sheets',
                children: [
                  {
                    labelKey: 'sidebar.mlSheetsList',
                    path: '/ml-sheets',
                    visible: showMlDispatchNav,
                  },
                  {
                    labelKey: 'sidebar.ogclTransferRequests',
                    path: '/ogcl-transfer-requests',
                    visible: true,
                  },
                ],
              }
            : showOgclTransferRequests && !showMlDispatchNav
              ? {
                  path: '/ogcl-transfer-requests',
                  type: 'single',
                }
              : {
                  path: '/ml-sheets',
                  type: 'single',
                }),
        },
        {
          id: 'masterLoadingSheetExternal',
          labelKey: 'sidebar.masterLoadingSheet',
          path: '/ml-sheets',
          visible: hasShipmentAccess && shipmentRoles.includes(user.role) && isExternalNetwork && allowModule('dispatch'),
          type: 'single',
        },
        {
          id: 'shipmentCrossing',
          labelKey: 'sidebar.shipmentCrossing',
          path: '/shipment-crossing',
          visible: hasShipmentAccess && hasShipmentCrossingAccess(user) && allowModule('crossing'),
          type: 'single',
        },
        {
          id: 'updateStatus',
          labelKey: 'sidebar.updateStatus',
          path: '/update-status',
          visible: hasShipmentAccess
            && ['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin', 'Contract Party Admin'].includes(user.role)
            && (user.role !== 'Agent' || user.location_hub_allow_delivery !== false)
            && allowModule('update_status'),
          type: 'single',
        },
        {
          id: 'trackShipment',
          labelKey: 'sidebar.trackShipment',
          path: '/user/track-shipment',
          visible: hasShipmentAccess && ['System Admin', 'Admin', 'Accountant', 'Agent', 'Driver', 'Hub Admin'].includes(user.role) && allowModule('track_shipment'),
          type: 'single',
        },
        {
          id: 'cashbook',
          labelKey: 'sidebar.cashbook',
          path: '/cashbook',
          visible: hasCashbookAccess && isOpsAdmin,
          type: 'single'
        },
        {
          id: 'dailyPlDesk',
          labelKey: 'sidebar.dailyPlDesk',
          path: '/pl-desk',
          visible: hasExpensesAccess && isAdminOrAccountant && allowModule('daily_pl'),
          type: 'single',
        },
        {
          id: 'plStatutory',
          labelKey: 'sidebar.plStatutory',
          path: '/pl-statutory',
          visible: hasExpensesAccess && isAdminOrAccountant && allowModule('pl_statutory'),
          type: 'single',
        },
        {
          id: 'externalMLSheet',
          labelKey: 'sidebar.externalMLSheet',
          path: '/external-ml',
          visible: hasShipmentAccess && isOpsAdmin && hasExternalML && allowModule('external_ml'),
          type: 'single'
        },
        {
          id: 'settlements',
          labelKey: showAgentSettlements && !showOpsSettlements
            ? 'sidebar.settleWithCompany'
            : 'sidebar.settlementApprovals',
          path: showAgentSettlements && !showOpsSettlements
            ? '/company-settlements'
            : '/company-settlement-approvals',
          visible: (showOpsSettlements || showAgentSettlements) && allowModule('settlements'),
          type: 'single',
        },
        {
          id: 'locationHubVehicle',
          labelKey: 'sidebar.locationHubVehicle',
          visible: hasLocationHubVehicleMenu && allowModule('location_hub'),
          type: 'dropdown',
          defaultPath: (hasShipmentAccess && isOpsAdmin) ? '/location-hubs' : '/vehicle-owners',
          children: [
            { labelKey: 'sidebar.locationHubs', path: '/location-hubs', visible: hasShipmentAccess && isOpsAdmin },
            { labelKey: 'sidebar.vehicle', path: '/vehicles', visible: hasShipmentAccess && isOpsAdmin },
            { labelKey: 'sidebar.vehicleOwner', path: '/vehicle-owners', visible: hasVehicleOwnerAccess && isAdminOrAccountant },
            { labelKey: 'sidebar.routes', path: '/routes', visible: hasShipmentAccess && isOpsAdmin },
          ]
        },
        {
          id: 'shipmentCommission',
          labelKey: 'sidebar.shipmentCommission',
          visible: showExternalCommission && isAdminOrAccountant,
          type: 'dropdown',
          defaultPath: '/external-commissions',
          children: [
            {
              labelKey: 'sidebar.commissionDashboard',
              path: externalDashboardPath,
              visible: showExternalCommission && isAdminOrAccountant,
            },
            { labelKey: 'sidebar.commissionRecord', path: '/external-commissions', visible: showExternalCommission && isAdminOrAccountant },
            { labelKey: 'sidebar.agentBulkSettlement', path: '/external-commissions/bulk-settlement', visible: showExternalCommission && isAdminOrAccountant },
            {
              labelKey: 'sidebar.hubInsights',
              path: '/external-commissions/hub-insights',
              visible: showExternalCommission && ['System Admin', 'Admin', 'Accountant'].includes(user.role),
            },
          ]
        },
        {
          id: 'userManagement',
          labelKey: 'sidebar.userManagement',
          visible: (hasShipmentAccess || hasAttendanceAccessFlag || isOpsAdmin) && shipmentRoles.includes(user.role) && allowModule('users'),
          type: 'dropdown',
          defaultPath: '/users',
          children: [
            { labelKey: 'sidebar.users', path: '/users', visible: shipmentRoles.includes(user.role) },
            { labelKey: 'sidebar.clientsVendors', path: '/shipment-clients', visible: hasShipmentAccess && shipmentRoles.includes(user.role) }
          ]
        },
        {
          id: 'hrWorkforce',
          labelKey: 'sidebar.hrWorkforce',
          visible: hasAttendanceAccessFlag && shipmentRoles.includes(user.role),
          type: 'dropdown',
          children: [
            { labelKey: 'sidebar.attendance', path: '/attendance', visible: hasAttendanceAccessFlag && shipmentRoles.includes(user.role) },
            { labelKey: 'sidebar.bulkLoginLogout', path: '/attendance/bulk-login', visible: hasAttendanceAccessFlag && ['System Admin', 'Admin', 'Accountant'].includes(user.role) },
            { labelKey: 'sidebar.salaryPayroll', path: '/salaries', visible: hasAttendanceAccessFlag && shipmentRoles.includes(user.role) }
          ]
        },
        // Expenses + Loans are separate can_access flags.
        // Company agents get expenses only (no loans, no cashbook).
        ...(showExpenses && !showLoans
          ? [{
              id: 'expenseManagement',
              labelKey: 'sidebar.expenseManagement',
              path: '/expenses',
              visible: true,
              type: 'single',
            }]
          : [{
              id: 'expenseManagement',
              labelKey: 'sidebar.expenseManagement',
              visible: showExpenses || showLoans,
              type: 'dropdown',
              defaultPath: showExpenses ? '/expenses' : '/loans-given',
              children: [
                { labelKey: 'sidebar.expenses', path: '/expenses', visible: showExpenses },
                { labelKey: 'sidebar.loans', path: '/loans-given', visible: showLoans },
                { labelKey: 'sidebar.loansReceived', path: '/loans-received', visible: showLoans },
              ],
            }]),
        {
          id: 'weeklyBills',
          labelKey: 'sidebar.weeklyBills',
          path: '/expenses/weekly-bills',
          visible: showWeeklyBills && allowModule('weekly_bills'),
          type: 'single',
        },
        {
          id: 'dailyPayments',
          labelKey: 'sidebar.dailyPayments',
          path: '/expenses/payment-queue',
          visible: showDailyPayments,
          type: 'single',
        },
        {
          id: 'misReports',
          labelKey: 'sidebar.misReports',
          path: '/reports',
          visible: (isOpsAdmin || isAgent) && allowModule('mis_reports') && (
            hasShipmentAccess
            || hasExpensesAccess
            || hasAttendanceAccessFlag
            || hasOnContract
            || hasOnContractTruck
            || showMlAgentCommission
            || hasCommissionAccess
            || showExternalCommission
          ),
          type: 'single'
        },
        {
          id: 'analyticalReports',
          labelKey: 'sidebar.analyticalReports',
          path: '/analytics',
          visible: (isOpsAdmin || user.role === 'Agent') && hasAnalyticalReports && (isSystemAdmin || hasShipmentAccess || hasCommissionAccess || hasExpensesAccess || hasOnContract || hasOnContractTruck),
          type: 'single'
        },
        {
          id: 'onContract',
          labelKey: 'sidebar.onContract',
          path: '/on-contracts',
          visible: hasOnContract && isAdminOrAccountant,
          type: 'single'
        },
        {
          id: 'contractManagement',
          labelKey: 'sidebar.contractManagement',
          visible: hasOnContractTruck && isAdminOrAccountant,
          type: 'dropdown',
          defaultPath: '/contract-parties',
          children: [
            { labelKey: 'sidebar.contractParty', path: '/contract-parties', visible: hasOnContractTruck && isAdminOrAccountant },
            { labelKey: 'sidebar.vehicleContract', path: '/vehicle-contracts', visible: hasOnContractTruck && isAdminOrAccountant }
          ]
        },
        {
          id: 'agentNetwork',
          labelKey: 'sidebar.agentNetwork',
          visible: hasAgentNetwork && isAdminOrAccountant && allowModule('agent_network'),
          type: 'dropdown',
          children: [
            { labelKey: 'sidebar.partnerAgents', path: '/partner-agents', visible: hasAgentNetwork && isAdminOrAccountant },
            { labelKey: 'sidebar.findAgent', path: '/partner-agents/find', visible: hasAgentNetwork && isAdminOrAccountant },
            { labelKey: 'sidebar.requests', path: '/partner-agents/requests', visible: hasAgentNetwork && isAdminOrAccountant },
          ]
        },
        {
          id: 'myPartnerships',
          labelKey: 'sidebar.myPartnerships',
          path: '/my-partnerships',
          visible: user.role === 'Agent' && hasMyPartnerships,
          type: 'single'
        },
        {
          id: 'contentManagement',
          labelKey: 'sidebar.contentManagement',
          visible: isSystemAdmin,
          type: 'dropdown',
          children: [
            { labelKey: 'sidebar.testimonials', path: '/testimonials', visible: isSystemAdmin },
            { labelKey: 'sidebar.contactMessages', path: '/contact', visible: isSystemAdmin }
          ]
        },
      ];

      const visibleItems = items.filter(item => item.visible);

      if (isAccountant) {
        setMenuItems(
          visibleItems
            .filter(item => ACCOUNTANT_ALLOWED_ITEM_IDS.has(item.id))
            .map(item => {
              if (item.id !== 'expenseManagement' || item.type !== 'dropdown') return item;
              const expensesChild = (item.children || []).find(
                child => child.path === '/expenses' && child.visible,
              );
              if (!expensesChild) {
                return showExpenses
                  ? {
                      id: item.id,
                      labelKey: item.labelKey,
                      path: '/expenses',
                      visible: true,
                      type: 'single',
                    }
                  : null;
              }
              return {
                id: item.id,
                labelKey: item.labelKey,
                path: '/expenses',
                visible: true,
                type: 'single',
              };
            })
            .filter(Boolean),
        );
      } else {
        setMenuItems(visibleItems);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;

    // Batch listing (including OCR_PENDING) runs only on the settlement page.
    // Do not poll it from the sidebar — every logged-in account would hit it
    // on each navigation and on a timer.
    const isAgent = user.role === 'Agent';
    if (!isAgent || user.network_mode === 'EXTERNAL') {
      setPendingNavDots({});
      return undefined;
    }

    let cancelled = false;

    const loadPendingDots = async () => {
      const dots = {
        [PENDING_DOT_PATHS.companySettlementApprovals]: false,
        [PENDING_DOT_PATHS.companySettlements]: false,
      };

      try {
        const sumRes = await companySettlementService.getSummary();
        const data = sumRes?.data || {};
        const pas = Number(data?.paid?.amount_outstanding || 0);
        const twoP = Number(data?.to_pay?.amount_outstanding || 0);
        dots[PENDING_DOT_PATHS.companySettlements] = pas > 0 || twoP > 0;
      } catch {
        // ignore — sidebar should still render
      }

      if (!cancelled) {
        setPendingNavDots(dots);
      }
    };

    loadPendingDots();
    const interval = window.setInterval(loadPendingDots, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [user]);

  useEffect(() => {
    if (user && isOpen) {
      const activeDropdown = menuItems.find(item => {
        if (item.type === 'dropdown') {
          return item.children?.some(child => child.visible && isActive(child.path));
        }
        return false;
      });

      if (activeDropdown && openDropdown !== activeDropdown.id) {
        setOpenDropdown(activeDropdown.id);
      }
    }
  }, [location.pathname, menuItems, user, isOpen]);

  const isActive = (path) => location.pathname.startsWith(path);

  const findActiveNavTargetId = useCallback(() => {
    let bestId = null;
    let bestLength = 0;

    menuItems.forEach((item) => {
      if (item.type === 'single' && item.path && isActive(item.path)) {
        if (item.path.length >= bestLength) {
          bestLength = item.path.length;
          bestId = `single-${item.id}`;
        }
      }
      if (item.type === 'dropdown') {
        (item.children || []).forEach((child) => {
          if (child.visible && child.path && isActive(child.path)) {
            if (child.path.length >= bestLength) {
              bestLength = child.path.length;
              bestId = `child-${pathToNavKey(child.path)}`;
            }
          }
        });
      }
    });

    return bestId;
  }, [menuItems, location.pathname]);

  useEffect(() => {
    if (!isOpen || window.innerWidth >= 1024 || menuItems.length === 0) return undefined;

    const scrollToActiveItem = () => {
      const navEl = navRef.current;
      if (!navEl) return;

      const targetId = findActiveNavTargetId();
      if (!targetId) return;

      const activeEl = navEl.querySelector(`[data-nav-id="${targetId}"]`);
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    };

    const timer = window.setTimeout(scrollToActiveItem, 150);
    return () => window.clearTimeout(timer);
  }, [isOpen, openDropdown, menuItems, location.pathname, findActiveNavTargetId]);

  const isDropdownActive = (children) => {
    return children?.some(child => child.visible && isActive(child.path));
  };

  const hasPendingDot = (path) => Boolean(path && pendingNavDots[path]);

  const dropdownHasPendingDot = (children) =>
    children?.some((child) => child.visible && hasPendingDot(child.path));

  const isMobileView = () => window.innerWidth < 1024;

  const keepSidebarOpenOnMobile = (itemId) => MOBILE_KEEP_SIDEBAR_OPEN_IDS.has(itemId);

  const handleNavigation = (path, { closeOnMobile = true } = {}) => {
    navigate(path);
    if (isMobileView() && closeOnMobile) {
      closeSidebar();
    }
  };

  const toggleDropdown = (itemId) => {
    setOpenDropdown(openDropdown === itemId ? null : itemId);
  };

  const handleDropdownLabelClick = (item) => {
    if (item.defaultPath) {
      setOpenDropdown(item.id);
      handleNavigation(item.defaultPath, { closeOnMobile: !keepSidebarOpenOnMobile(item.id) });
      return;
    }
    toggleDropdown(item.id);
  };

  const handleChevronClick = (event, itemId) => {
    event.stopPropagation();
    toggleDropdown(itemId);
  };

  const handleChildNavigation = (path) => {
    handleNavigation(path);
  };

  if (loading || !user) {
    return (
      <>
        <aside className="hidden lg:flex w-[300px] bg-white border-r border-gray-200 h-full flex-col pt-4 shrink-0 overflow-hidden">
          <div className="px-4 space-y-3">
            <div className="h-8 bg-gray-100 rounded-lg animate-pulse" />
            <div className="h-8 bg-gray-100 rounded-lg animate-pulse w-3/4" />
            <div className="h-8 bg-gray-100 rounded-lg animate-pulse w-2/3" />
          </div>
        </aside>
      </>
    );
  }

  return (
    <>
      {/* Mobile overlay — no backdrop-blur so header stays sharp */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[60] lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative top-0 left-0 z-[70] lg:z-auto
          h-screen lg:h-auto w-[280px] md:w-[300px] bg-white border-r border-gray-200
          flex flex-col shrink-0 overflow-hidden
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Sidebar header — logo + close button on mobile */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0 lg:hidden">
          <AppBrandLogo companyLogo={companyLogo} variant="sidebarMobile" />
          <Button
            variant="ghost"
            onClick={closeSidebar}
            className="p-1.5 rounded-lg"
            aria-label={t('header.closeMenu')}
          >
            <X className="w-5 h-5 text-gray-500" />
          </Button>
        </div>

        {/* Nav items */}
        <nav ref={navRef} className="sidebar-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-3 px-3 space-y-1">
          {menuItems.map((item) => {
            const itemLabel = t(item.labelKey);
            if (item.type === 'single') {
              const active = isActive(item.path);
              return (
                <Button
                  key={item.path}
                  variant="ghost"
                  data-nav-id={`single-${item.id}`}
                  onClick={() => handleNavigation(item.path)}
                  className={`group w-full justify-start gap-3 px-3 py-2.5 rounded-lg text-sm md:whitespace-normal ${
                    active
                      ? 'bg-blue-50 text-blue-700 font-semibold'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  title={itemLabel}
                >
                  <NavIcon labelKey={item.labelKey} active={active} />
                  <span className="truncate md:truncate-none md:leading-snug">{itemLabel}</span>
                </Button>
              );
            }

            if (item.type === 'dropdown') {
              const dropdownActive = isDropdownActive(item.children);
              const isDropdownOpen = openDropdown === item.id;

              return (
                <div key={item.id}>
                  <div
                    className={`group flex items-stretch rounded-lg transition-colors duration-200 ${
                      dropdownActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Button
                      variant="ghost"
                      onClick={() => handleDropdownLabelClick(item)}
                      className={`flex-1 min-w-0 md:min-w-fit justify-start gap-3 px-3 py-2.5 rounded-lg rounded-r-none !bg-transparent hover:!bg-transparent active:!bg-transparent text-sm md:whitespace-normal ${
                        dropdownActive
                          ? 'text-blue-700 font-semibold'
                          : 'text-gray-600 group-hover:text-gray-900'
                      }`}
                      title={itemLabel}
                    >
                      <NavIcon labelKey={item.labelKey} active={dropdownActive} />
                      <NavLabel label={itemLabel} showDot={dropdownHasPendingDot(item.children)} />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={(event) => handleChevronClick(event, item.id)}
                      className={`shrink-0 px-2 py-2.5 rounded-lg rounded-l-none !bg-transparent hover:!bg-transparent active:!bg-transparent ${
                        dropdownActive
                          ? 'text-blue-700'
                          : 'text-gray-500 group-hover:text-gray-800'
                      }`}
                      aria-label={isDropdownOpen ? 'Collapse section' : 'Expand section'}
                      aria-expanded={isDropdownOpen}
                    >
                      <ChevronDown
                        className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
                          isDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </Button>
                  </div>

                  {isDropdownOpen && (
                    <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-gray-100 pl-3">
                      {item.children.filter(child => child.visible).map((child) => {
                        const childActive = isActive(child.path);
                        const childLabel = child.label || t(child.labelKey);
                        return (
                          <Button
                            key={child.path}
                            variant="ghost"
                            data-nav-id={`child-${pathToNavKey(child.path)}`}
                            onClick={() => handleChildNavigation(child.path)}
                            className={`group w-full justify-start gap-2 px-2 py-2 rounded-md text-sm md:whitespace-normal ${
                              childActive
                                ? 'text-blue-700 font-semibold bg-blue-50/50'
                                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                            }`}
                          >
                            <NavIcon labelKey={child.labelKey} size="sm" active={childActive} />
                            <NavLabel label={childLabel} showDot={hasPendingDot(child.path)} />
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
